/**
 * @fileoverview Mobile Bottom Navigation Component
 *
 * Provides a fixed bottom navigation bar for mobile devices with icon-based
 * navigation following mobile-first design patterns (like Facebook, Instagram, etc.)
 *
 * @module layout/app/AppFooterNav
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge } from '@xanectech/ui/components';
import { cn } from '@xanectech/ui/lib';
import { AppFooterNavProps, FooterNavItem } from './app-config';
import { useEncryptionStatus } from '@xanectech/auth';

/**
 * AppFooterNav Component
 *
 * Mobile-first bottom navigation bar with icon buttons.
 * - Fixed at bottom of viewport
 * - Touch-friendly 44px minimum tap targets
 * - Active state indicators
 * - Hidden on desktop (md breakpoint and above)
 * - Supports badges for notifications
 *
 * @component
 * @example
 * ```tsx
 * const items: FooterNavItem[] = [
 *   { id: 'home', label: 'Home', icon: Home, url: '/dashboard' },
 *   { id: 'messages', label: 'Messages', icon: MessageSquare, url: '/messages', badge: 3 }
 * ];
 *
 * <AppFooterNav items={items} activeItem="home" />
 * ```
 */
export const AppFooterNav: React.FC<AppFooterNavProps> = ({
  items,
  activeItem,
  onItemClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isUnlocked } = useEncryptionStatus();

  // Filter items based on lock state
  const visibleItems = items.filter((item) => {
    if (!isUnlocked && item.shownWhenLocked !== true) {
      return false;
    }
    return true;
  });

  // Determine active item based on current route if not explicitly provided
  const getActiveItem = (): string => {
    if (activeItem) return activeItem;

    const currentPath = location.pathname;
    const active = visibleItems.find(
      (item) =>
        currentPath === item.url || currentPath.startsWith(item.url + '/'),
    );
    return active?.id || '';
  };

  const currentActive = getActiveItem();

  const handleItemClick = (item: FooterNavItem) => {
    if (onItemClick) {
      onItemClick(item.id);
    }
    void navigate(item.url);
  };

  return (
    <nav
      className='fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border md:hidden pb-[env(safe-area-inset-bottom)]'
      role='navigation'
      aria-label='Bottom navigation'
    >
      <div className='flex items-center justify-around h-16 px-2'>
        {visibleItems.map((item) => {
          const isActive = currentActive === item.id;
          const Icon = item.icon;

          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              className={cn(
                'relative flex flex-col items-center justify-center',
                'min-w-[44px] min-h-[44px] px-3 py-2',
                'rounded-lg transition-all duration-200',
                'active:scale-95',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <div className='relative'>
                <Icon
                  className={cn(
                    'h-6 w-6 transition-all',
                    isActive && 'scale-110',
                  )}
                  strokeWidth={isActive ? 2.5 : 2}
                />
                {item.badge && item.badge > 0 && (
                  <Badge
                    variant='destructive'
                    className='absolute -top-2 -right-2 h-4 min-w-4 flex items-center justify-center px-1 text-[10px] font-semibold'
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </Badge>
                )}
              </div>
              <span
                className={cn(
                  'text-[10px] font-medium mt-1 leading-none',
                  isActive && 'font-semibold',
                )}
              >
                {item.label}
              </span>
              {isActive && (
                <div className='absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-primary rounded-full' />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};
